import { useMemo, useState } from 'react';
import Checkbox from '../../../../core/components/atoms/Checkbox';
import { Icon } from '../../../../core/components/atoms/Icon';
import MemoryButton from '../memory-button';
import { Modal } from '../modal';
import { reviewService } from '../../queries/review';
import { useUserStore } from '../../../Auth/stores/authStore';
import { useReviewStore } from '../../stores/review';
import { useNavigate } from 'react-router-dom';
import useDecksAvailable from '../../hooks/use-decks-available';

const ModalStartReview = ({
  decks,
}: {
  decks: {
    deckUuid: string;
    title: string;
    totalFlashcards: number;
    totalFlashcardsNew: number;
    totalFlashcardsReviewed: number;
  }[];
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();
  const { setReview } = useReviewStore();
  const { user } = useUserStore();
  const userUuid = user?.memoryUserUuid;

  const [selectedDecks, setSelectedDecks] = useState<string[]>([]);

  const { decksAvailable, isLoadingDecksAvailable } = useDecksAvailable();

  const handleSelectDeck = () => {
    if (selectedDecks.length === decks.length) {
      setSelectedDecks([]);
    } else {
      setSelectedDecks(decks.map((deck) => deck.deckUuid));
    }
  };

  const onHandleSelectDeck = (deck: string) => {
    setSelectedDecks((prev) => {
      const isSelected = prev.includes(deck);

      return isSelected ? prev.filter((d) => d !== deck) : [...prev, deck];
    });
  };

  const onStartReview = async () => {
    if (!userUuid) return;

    try {
      const result = await reviewService.getFlashcardToReview(
        userUuid,
        selectedDecks
      );

      if (!result.length) return;

      setReview({
        flashcards: result,
        startReviewAt: new Date(),
        qtdFlashcards: result.length,
        qtdFlashcardsReviewed: 1,
        currentFlashcard: result[0],
        decksUuids: selectedDecks,
      });
      navigate('/memory/review-flashcards');
      setIsOpen(false);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      <MemoryButton
        label='Iniciar Revisão'
        variant='primary'
        className='w-full'
        onClick={() => setIsOpen(true)}
        disabled={!decksAvailable?.hasAvailableFlashcards}
      />
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title='Iniciar Revisão'
      >
        <div className='flex flex-col gap-4'>
          <div>
            <label
              htmlFor='deck-name'
              className='text-md text-mono-color-darkest'
            >
              Selecione os decks que você gostaria de adicionar para a sua
              sessão de revisão.
            </label>
          </div>
          <div
            onClick={handleSelectDeck}
            className='flex cursor-pointer flex-row items-center gap-2 text-h5 text-button-color-tertiary-default'
          >
            <Icon Icon={'check'} type='material' />
            <span>
              {selectedDecks.length === decks.length
                ? 'Desmarcar todos'
                : 'Selecionar todos'}
            </span>
          </div>

          <div className='grid max-h-[10rem] grid-cols-2 gap-2 overflow-y-auto lg:grid-cols-3'>
            {decks.map((deck) => (
              <div
                key={deck.deckUuid}
                className='border flex cursor-pointer flex-row items-center gap-2 rounded-md border-mono-color-light p-2'
                onClick={() => onHandleSelectDeck(deck.deckUuid)}
              >
                <Checkbox
                  checked={selectedDecks.includes(deck.deckUuid)}
                  id={deck.deckUuid}
                />
                <span className='text-mono-color-darkest'>{deck.title}</span>
              </div>
            ))}
          </div>

          <div className='flex flex-row gap-2'>
            <MemoryButton
              label='Cancelar'
              onClick={() => setIsOpen(false)}
              variant='outline'
              className='w-full'
              allowReadOnly
            />
            <MemoryButton
              label='Iniciar'
              variant='primary'
              className='w-full bg-brand-color-primary-pure disabled:cursor-not-allowed disabled:bg-brand-color-primary-pure/50 disabled:text-card-color-default'
              onClick={onStartReview}
              disabled={!selectedDecks.length}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ModalStartReview;
